import React, { useState, useEffect, useCallback, useRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import FashionApiService from '../../../../services/FashionApiService'
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext'
import {
  SalesStockContainer,
  StatsGroup,
  ViewDropdown,
  ViewButton,
  ViewPanel,
  ViewChip,
  FilterWrapper,
  FilterButton,
  FilterBadge,
  FilterPanel,
  FilterRow,
  FilterLabel,
  FilterChips,
  FilterChip,
  FilterFooter,
  HeaderActions,
  ActionButton,
  PrimaryButton,
  TableCard,
  TableWrapper,
  StyledTable,
  GroupRow,
  Badge,
  OpTag,
  PedTag,
  RefCode,
  TamBadge,
  ColorDot,
  QrButton,
  GroupArrow,
  ProgressContainer,
  ProgressBar,
  ProgressFill,
  EmptyState,
  QrModalOverlay,
  QrModalContent,
  LoadingContainer,
} from './style'

export interface SalesStockItem {
  id: number
  status: string
  serviceId: number
  fashionOrderId: number
  size: string
  deliveryDate: string | null
  qrCodeData: string | null
  FashionProduct?: {
    id: number
    name: string
    referenceProduct: string | null
    productCode: string | null
    Collection?: { name: string } | null
    Episode?: { name: string } | null
  } | null
  CollectionColor?: {
    id: number
    name: string
    Pantone?: { hex: string | null } | null
  } | null
}

export interface FilterState {
  marca: string
  colecao: string
  ep: string
  tam: string
  status: string
  cor: string
}

export interface FilterOptions {
  marcas: string[]
  colecoes: string[]
  eps: string[]
  tamanhos: string[]
  status: string[]
  cores: string[]
}

export interface SalesStockState {
  stats: { ops: number; pedidos: number; pecas: number }
  currentView: 'produto' | 'op' | 'pedido'
  setCurrentView: (v: 'produto' | 'op' | 'pedido') => void
  filters: FilterState
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>
  filterOptions: FilterOptions
  filteredCount: number
  fetchData: () => void
  expandAll: () => void
  collapseAll: () => void
  exportCSV: () => void
}

const STATUS_MAP: Record<string, { variant: string; order: number }> = {
  AGUARDANDO_CORTE: { variant: 'gray', order: 1 },
  EM_CORTE: { variant: 'purple', order: 2 },
  COSTURA: { variant: 'blue', order: 3 },
  ACABAMENTO: { variant: 'orange', order: 4 },
  REVISAO: { variant: 'yellow', order: 5 },
  EXPEDICAO: { variant: 'teal', order: 6 },
  PRODUTO_ACABADO: { variant: 'green', order: 7 },
}

const STATUS_LABELS: Record<string, string> = {
  AGUARDANDO_CORTE: 'Aguardando Corte',
  EM_CORTE: 'Em Corte',
  COSTURA: 'Costura',
  ACABAMENTO: 'Acabamento',
  REVISAO: 'Revisao',
  EXPEDICAO: 'Expedicao',
  PRODUTO_ACABADO: 'Produto Acabado',
}

const VIEW_LABELS: Record<string, string> = {
  produto: 'Produto',
  op: 'OP',
  pedido: 'Pedido',
}

const QR_ICON = (
  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
    <rect x="3" y="3" width="6" height="6" rx="1" />
    <rect x="15" y="3" width="6" height="6" rx="1" />
    <rect x="3" y="15" width="6" height="6" rx="1" />
    <rect x="10" y="10" width="2" height="2" />
    <rect x="14" y="10" width="2" height="2" />
    <rect x="18" y="10" width="2" height="2" />
    <rect x="10" y="14" width="2" height="2" />
    <rect x="14" y="14" width="2" height="2" />
    <rect x="10" y="18" width="2" height="2" />
    <rect x="18" y="18" width="2" height="2" />
  </svg>
)

export const SalesStockToolbar: React.FC<{ state: SalesStockState }> = ({ state }) => {
  const { stats, currentView, setCurrentView, filters, setFilters, filterOptions, filteredCount, fetchData, expandAll, collapseAll, exportCSV } = state

  const [viewPanelOpen, setViewPanelOpen] = useState(false)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const viewRef = useRef<HTMLDivElement>(null)
  const filterRef = useRef<HTMLDivElement>(null)

  const activeFilterCount = Object.entries(filters).filter(([, v]) => !!v).length

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (viewRef.current && !viewRef.current.contains(e.target as Node)) setViewPanelOpen(false)
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setFilterPanelOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSetFilter = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: prev[key] === value ? '' : value }))
  }

  const clearFilters = () => {
    setFilters({ marca: '', colecao: '', ep: '', tam: '', status: '', cor: '' })
  }

  return (
    <>
      <StatsGroup>
        <span><strong>{stats.ops}</strong> OPs</span>
        <span><strong>{stats.pedidos}</strong> pedidos</span>
        <span><strong>{stats.pecas}</strong> peças</span>
      </StatsGroup>

      <ViewDropdown ref={viewRef}>
        <ViewButton onClick={() => setViewPanelOpen(!viewPanelOpen)}>
          {VIEW_LABELS[currentView]}
          <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </ViewButton>
        <ViewPanel $open={viewPanelOpen}>
          {(['produto', 'op', 'pedido'] as const).map(v => (
            <ViewChip key={v} $active={currentView === v} onClick={() => { setCurrentView(v); setViewPanelOpen(false) }}>
              {VIEW_LABELS[v]}
            </ViewChip>
          ))}
        </ViewPanel>
      </ViewDropdown>

      <FilterWrapper ref={filterRef}>
        <FilterButton $active={activeFilterCount > 0} onClick={() => setFilterPanelOpen(!filterPanelOpen)}>
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="11" y1="18" x2="13" y2="18" />
          </svg>
          Filtros
          {activeFilterCount > 0 && <FilterBadge>{activeFilterCount}</FilterBadge>}
        </FilterButton>

        <FilterPanel $open={filterPanelOpen}>
          <FilterRow>
            <FilterLabel>Marca</FilterLabel>
            <FilterChips>
              <FilterChip $active={!filters.marca} onClick={() => setFilters(prev => ({ ...prev, marca: '' }))}>Todas</FilterChip>
              {filterOptions.marcas.map(m => (
                <FilterChip key={m} $active={filters.marca === m} onClick={() => handleSetFilter('marca', m)}>{m}</FilterChip>
              ))}
            </FilterChips>
          </FilterRow>
          <FilterRow>
            <FilterLabel>Coleção</FilterLabel>
            <FilterChips>
              <FilterChip $active={!filters.colecao} onClick={() => setFilters(prev => ({ ...prev, colecao: '' }))}>Todas</FilterChip>
              {filterOptions.colecoes.map(c => (
                <FilterChip key={c} $active={filters.colecao === c} onClick={() => handleSetFilter('colecao', c)}>{c}</FilterChip>
              ))}
            </FilterChips>
          </FilterRow>
          <FilterRow>
            <FilterLabel>Entrega</FilterLabel>
            <FilterChips>
              <FilterChip $active={!filters.ep} onClick={() => setFilters(prev => ({ ...prev, ep: '' }))}>Todas</FilterChip>
              {filterOptions.eps.map(e => (
                <FilterChip key={e} $active={filters.ep === e} onClick={() => handleSetFilter('ep', e)}>{e}</FilterChip>
              ))}
            </FilterChips>
          </FilterRow>
          <FilterRow>
            <FilterLabel>Tamanho</FilterLabel>
            <FilterChips>
              <FilterChip $active={!filters.tam} onClick={() => setFilters(prev => ({ ...prev, tam: '' }))}>Todos</FilterChip>
              {filterOptions.tamanhos.map(t => (
                <FilterChip key={t} $active={filters.tam === t} onClick={() => handleSetFilter('tam', t)}>{t}</FilterChip>
              ))}
            </FilterChips>
          </FilterRow>
          <FilterRow>
            <FilterLabel>Status</FilterLabel>
            <FilterChips>
              <FilterChip $active={!filters.status} onClick={() => setFilters(prev => ({ ...prev, status: '' }))}>Todos</FilterChip>
              {filterOptions.status.map(s => (
                <FilterChip key={s} $active={filters.status === s} onClick={() => handleSetFilter('status', s)}>
                  {STATUS_LABELS[s] || s}
                </FilterChip>
              ))}
            </FilterChips>
          </FilterRow>
          <FilterRow>
            <FilterLabel>Cor</FilterLabel>
            <FilterChips>
              <FilterChip $active={!filters.cor} onClick={() => setFilters(prev => ({ ...prev, cor: '' }))}>Todas</FilterChip>
              {filterOptions.cores.map(c => (
                <FilterChip key={c} $active={filters.cor === c} onClick={() => handleSetFilter('cor', c)}>{c}</FilterChip>
              ))}
            </FilterChips>
          </FilterRow>
          <FilterFooter>
            <span style={{ fontSize: 12, color: '#999' }}>{filteredCount} peça{filteredCount !== 1 ? 's' : ''}</span>
            <ActionButton onClick={clearFilters}>Limpar filtros</ActionButton>
          </FilterFooter>
        </FilterPanel>
      </FilterWrapper>

      <HeaderActions>
        {currentView !== 'produto' && (
          <>
            <ActionButton onClick={expandAll}>Expandir tudo</ActionButton>
            <ActionButton onClick={collapseAll}>Recolher tudo</ActionButton>
          </>
        )}
        <ActionButton onClick={exportCSV}>
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Exportar
        </ActionButton>
        <PrimaryButton onClick={fetchData}>
          <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          Atualizar
        </PrimaryButton>
      </HeaderActions>
    </>
  )
}

interface DashboardProps {
  state: SalesStockState
  items: SalesStockItem[]
  filtered: SalesStockItem[]
  isLoading: boolean
  expandedGroups: Record<string, boolean>
  setExpandedGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
}

const SalesStockDashboard: React.FC<DashboardProps> = ({ state, items, filtered, isLoading, expandedGroups, setExpandedGroups }) => {
  const { currentView } = state
  const [qrModalData, setQrModalData] = useState<{ data: string; label: string } | null>(null)

  const handleQrClick = (item: SalesStockItem) => {
    const ref = item.FashionProduct?.referenceProduct ?? ''
    const mod = item.FashionProduct?.name ?? ''
    const cor = item.CollectionColor?.name ?? ''
    const op = `OP-${String(item.serviceId).padStart(3, '0')}`
    const qrData = JSON.stringify({
      id: item.id,
      ref,
      mod,
      tam: item.size,
      cor,
      op,
      pedido: item.fashionOrderId,
    })
    setQrModalData({ data: qrData, label: `${ref} - ${mod} (${item.size})` })
  }

  const renderStatusBadge = (status: string) => {
    const info = STATUS_MAP[status] || { variant: 'gray' }
    return <Badge $variant={info.variant}>{STATUS_LABELS[status] || status}</Badge>
  }

  const renderColorCell = (cor: string, hex?: string | null) => {
    const formatHex = (h?: string | null) => {
      if (!h) return '#ccc'
      const val = h.trim()
      return val.startsWith('#') ? val : (/^[0-9a-fA-F]{3,6}$/.test(val) ? `#${val}` : val)
    }
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <ColorDot $hex={formatHex(hex)} />
        {cor}
      </span>
    )
  }

  const renderGroupedView = () => {
    const groupKey = currentView === 'op' ? 'serviceId' : 'fashionOrderId'
    const groups: Record<string, SalesStockItem[]> = {}

    filtered.forEach(item => {
      const key = String((item as any)[groupKey])
      if (!groups[key]) groups[key] = []
      groups[key].push(item)
    })

    const sortedKeys = Object.keys(groups).sort()
    const rows: React.ReactNode[] = []

    sortedKeys.forEach(key => {
      const group = groups[key]
      const n = group.length
      const first = group[0]
      const isExpanded = expandedGroups[key] !== false
      const eps = [...new Set(group.map(r => r.FashionProduct?.Episode?.name))].filter(Boolean).join(', ')
      const minOrder = Math.min(...group.map(r => STATUS_MAP[r.status]?.order || 0))
      const worstStatus = Object.entries(STATUS_MAP).find(([, v]) => v.order === minOrder)?.[0] || group[0]?.status
      const opLabel = `OP-${String(key).padStart(3, '0')}`

      if (currentView === 'op') {
        rows.push(
          <GroupRow key={`group-${key}`} onClick={() => setExpandedGroups(prev => {
            const isCurrentlyExpanded = prev[key] !== false
            return { ...prev, [key]: !isCurrentlyExpanded }
          })}>
            <td><GroupArrow $collapsed={!isExpanded}>&#9660;</GroupArrow><OpTag>{opLabel}</OpTag></td>
            <td><RefCode>{first.FashionProduct?.referenceProduct ?? ''}</RefCode></td>
            <td>{first.FashionProduct?.productCode ?? ''}</td>
            <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{first.FashionProduct?.name ?? ''}</td>
            <td>{renderColorCell(first.CollectionColor?.name ?? '', first.CollectionColor?.Pantone?.hex)}</td>
            <td></td><td></td>
            <td><Badge $variant="blue">{eps}</Badge></td>
            <td>{renderStatusBadge(worstStatus)}</td>
            <td className="center">
              <span style={{ fontSize: 11, color: '#999', marginRight: 8 }}>{n} pc</span>
              <QrButton onClick={(e) => { e.stopPropagation(); setQrModalData({ data: `OP:${key}`, label: opLabel }) }}>{QR_ICON}</QrButton>
            </td>
          </GroupRow>
        )
      } else {
        const ENTREGUE = ['EXPEDICAO', 'PRODUTO_ACABADO']
        const nEnt = group.filter(r => ENTREGUE.includes(r.status)).length
        const pct = n > 0 ? Math.round(nEnt / n * 100) : 0
        const today = new Date(); today.setHours(0, 0, 0, 0)
        const prazo = first.deliveryDate ? new Date(first.deliveryDate) : null
        const daysLeft = prazo ? Math.ceil((prazo.getTime() - today.getTime()) / 86400000) : null

        let pctColor: string
        if (pct === 100) pctColor = '#065F46'
        else if (daysLeft === null) pctColor = '#696969'
        else if (daysLeft < 0) pctColor = '#DC2626'
        else if (daysLeft <= 7) pctColor = pct >= 50 ? '#D97706' : '#DC2626'
        else pctColor = '#065F46'

        rows.push(
          <GroupRow key={`group-${key}`} onClick={() => setExpandedGroups(prev => {
            const isCurrentlyExpanded = prev[key] !== false
            return { ...prev, [key]: !isCurrentlyExpanded }
          })}>
            <td><GroupArrow $collapsed={!isExpanded}>&#9660;</GroupArrow><PedTag>{key}</PedTag></td>
            <td><RefCode>{first.FashionProduct?.referenceProduct ?? ''}</RefCode></td>
            <td>{first.FashionProduct?.productCode ?? ''}</td>
            <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{first.FashionProduct?.name ?? ''}</td>
            <td>{renderColorCell(first.CollectionColor?.name ?? '', first.CollectionColor?.Pantone?.hex)}</td>
            <td></td><td></td>
            <td><Badge $variant="blue">{eps}</Badge></td>
            <td>{renderStatusBadge(worstStatus)}</td>
            <td className="center">
              <ProgressContainer title="Verde: no prazo | Amarelo: urgente (≤7 dias) | Vermelho: atrasado">
                <span style={{ fontSize: 12, fontWeight: 700, color: pctColor }}>{pct}%</span>
                <ProgressBar><ProgressFill $width={pct} $color={pctColor} /></ProgressBar>
                <span style={{ fontSize: 10, color: '#999' }}>{nEnt}/{n}</span>
              </ProgressContainer>
            </td>
            <td className="center">
              <QrButton onClick={(e) => { e.stopPropagation(); setQrModalData({ data: `PED:${key}`, label: key }) }}>{QR_ICON}</QrButton>
            </td>
          </GroupRow>
        )
      }

      if (isExpanded) {
        group.forEach(item => {
          const opLabelSub = `OP-${String(item.serviceId).padStart(3, '0')}`
          const ref = item.FashionProduct?.referenceProduct ?? ''
          const mod = item.FashionProduct?.name ?? ''
          const cor = item.CollectionColor?.name ?? ''
          const hex = item.CollectionColor?.Pantone?.hex
          const ep = item.FashionProduct?.Episode?.name ?? ''
          rows.push(
            <tr key={`item-${item.id}`}>
              {currentView === 'op' ? (
                <>
                  <td style={{ paddingLeft: 36 }}></td>
                  <td><RefCode>{ref}</RefCode></td>
                  <td>{item.FashionProduct?.productCode ?? ''}</td>
                  <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mod}</td>
                  <td>{renderColorCell(cor, hex)}</td>
                  <td className="center"><TamBadge>{item.size}</TamBadge></td>
                  <td><PedTag>{String(item.fashionOrderId)}</PedTag></td>
                  <td><Badge $variant="blue">{ep}</Badge></td>
                  <td>{renderStatusBadge(item.status)}</td>
                </>
              ) : (
                <>
                  <td style={{ paddingLeft: 36 }}></td>
                  <td><RefCode>{ref}</RefCode></td>
                  <td>{item.FashionProduct?.productCode ?? ''}</td>
                  <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mod}</td>
                  <td>{renderColorCell(cor, hex)}</td>
                  <td className="center"><TamBadge>{item.size}</TamBadge></td>
                  <td><OpTag>{opLabelSub}</OpTag></td>
                  <td><Badge $variant="blue">{ep}</Badge></td>
                  <td>{renderStatusBadge(item.status)}</td>
                  <td className="center">
                    {['EXPEDICAO', 'PRODUTO_ACABADO'].includes(item.status) ? <span style={{ color: '#065F46', fontWeight: 600 }}>&#10003;</span> : ''}
                  </td>
                </>
              )}
              <td className="center"><QrButton onClick={() => handleQrClick(item)}>{QR_ICON}</QrButton></td>
            </tr>
          )
        })
      }
    })

    return rows
  }

  const renderFlatView = () => {
    const sorted = [...filtered].sort((a, b) => {
      const refA = a.FashionProduct?.referenceProduct ?? ''
      const refB = b.FashionProduct?.referenceProduct ?? ''
      const corA = a.CollectionColor?.name ?? ''
      const corB = b.CollectionColor?.name ?? ''
      return refA.localeCompare(refB) || corA.localeCompare(corB) || a.size.localeCompare(b.size)
    })
    return sorted.map(item => {
      const ref = item.FashionProduct?.referenceProduct ?? ''
      const mod = item.FashionProduct?.name ?? ''
      const cor = item.CollectionColor?.name ?? ''
      const hex = item.CollectionColor?.Pantone?.hex ?? '#CCC'
      const op = `OP-${String(item.serviceId).padStart(3, '0')}`
      const ep = item.FashionProduct?.Episode?.name ?? ''
      return (
        <tr key={item.id}>
          <td><RefCode>{ref}</RefCode></td>
          <td>{item.FashionProduct?.productCode ?? ''}</td>
          <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{mod}</td>
          <td>{renderColorCell(cor, hex)}</td>
          <td className="center"><TamBadge>{item.size}</TamBadge></td>
          <td><OpTag>{op}</OpTag></td>
          <td><PedTag>{String(item.fashionOrderId)}</PedTag></td>
          <td><Badge $variant="blue">{ep}</Badge></td>
          <td>{renderStatusBadge(item.status)}</td>
          <td className="center"><QrButton onClick={() => handleQrClick(item)}>{QR_ICON}</QrButton></td>
        </tr>
      )
    })
  }

  const getColumns = () => {
    if (currentView === 'op') return ['OP', 'Referencia produto', 'Cód. Produto', 'Nome do Produto', 'Cor', 'Tam', 'Pedido', 'EP', 'Status', 'QR']
    if (currentView === 'pedido') return ['Pedido', 'Referencia produto', 'Cód. Produto', 'Nome do Produto', 'Cor', 'Tam', 'OP', 'EP', 'Status', 'Entrega', 'QR']
    return ['Referencia produto', 'Cód. Produto', 'Nome do Produto', 'Cor', 'Tam', 'OP', 'Pedido', 'EP', 'Status', 'QR']
  }

  const footerText = currentView === 'produto'
    ? `${filtered.length} peça${filtered.length !== 1 ? 's' : ''}`
    : currentView === 'op'
      ? `${Object.keys(filtered.reduce((acc: Record<string, boolean>, r) => { acc[`OP-${String(r.serviceId).padStart(3, '0')}`] = true; return acc }, {})).length} OPs · ${filtered.length} peças`
      : `${Object.keys(filtered.reduce((acc: Record<string, boolean>, r) => { acc[String(r.fashionOrderId)] = true; return acc }, {})).length} pedidos · ${filtered.length} peças`

  return (
    <SalesStockContainer>
      <TableCard>
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                {getColumns().map((col, i) => (
                  <th key={i} className={col === 'Tam' || col === 'QR' || col === 'Entrega' ? 'center' : ''}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={getColumns().length}><LoadingContainer>Carregando itens do estoque de vendas...</LoadingContainer></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={getColumns().length}><EmptyState>Nenhum resultado para os filtros aplicados.</EmptyState></td></tr>
              ) : currentView === 'produto' ? renderFlatView() : renderGroupedView()}
            </tbody>
          </StyledTable>
        </TableWrapper>
      </TableCard>

      {qrModalData && (
        <QrModalOverlay onClick={() => setQrModalData(null)}>
          <QrModalContent onClick={e => e.stopPropagation()}>
            <h3>QR Code</h3>
            <p>{qrModalData.label}</p>
            <QRCodeSVG value={qrModalData.data} size={200} />
            <button onClick={() => setQrModalData(null)}>Fechar</button>
          </QrModalContent>
        </QrModalOverlay>
      )}
    </SalesStockContainer>
  )
}

const EMPTY_SALES_FILTERS: FilterState = { marca: '', colecao: '', ep: '', tam: '', status: '', cor: '' }

export function useSalesStockState(enabled: boolean = true) {
  const { getPreference, setPreference, isLoading: isPrefsLoading } = useUserPreferences()
  const [items, setItems] = useState<SalesStockItem[]>([])
  const [filtered, setFiltered] = useState<SalesStockItem[]>([])
  const [stats, setStats] = useState({ ops: 0, pedidos: 0, pecas: 0 })
  const [isLoading, setIsLoading] = useState(enabled)
  const [currentView, setCurrentView] = useState<'produto' | 'op' | 'pedido'>('produto')
  const [filters, setFiltersLocal] = useState<FilterState>(EMPTY_SALES_FILTERS)
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ marcas: [], colecoes: [], eps: [], tamanhos: [], status: [], cores: [] })
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (isPrefsLoading) return
    const saved = getPreference<FilterState | undefined>('filters_sales_stock')
    if (saved) {
      setFiltersLocal(saved)
    }
  }, [isPrefsLoading, getPreference])

  const setFilters: React.Dispatch<React.SetStateAction<FilterState>> = useCallback((action) => {
    setFiltersLocal((prev) => {
      const next = typeof action === 'function' ? action(prev) : action
      setPreference('filters_sales_stock', next)
      return next
    })
  }, [setPreference])

  useEffect(() => {
    setExpandedGroups({})
  }, [currentView])

  const fetchData = useCallback(async () => {
    if (!enabled) return
    setIsLoading(true)
    try {
      const [itemsRes, statsRes, optionsRes] = await Promise.all([
        FashionApiService.getSalesStockItems(),
        FashionApiService.getSalesStockStats(),
        FashionApiService.getSalesStockFilterOptions(),
      ])
      setItems(itemsRes.data)
      setStats(statsRes.data)
      setFilterOptions(optionsRes.data)
    } catch (err) {
      console.error('Erro ao carregar dados do estoque de vendas:', err)
    } finally {
      setIsLoading(false)
    }
  }, [enabled])

  useEffect(() => {
    if (enabled) {
      fetchData()
    }
  }, [fetchData, enabled])

  useEffect(() => {
    let result = [...items]
    if (filters.marca) result = result.filter(r => r.FashionProduct?.name?.toLowerCase().includes(filters.marca.toLowerCase()))
    if (filters.colecao) result = result.filter(r => r.FashionProduct?.Collection?.name === filters.colecao)
    if (filters.ep) result = result.filter(r => r.FashionProduct?.Episode?.name === filters.ep)
    if (filters.tam) result = result.filter(r => r.size === filters.tam)
    if (filters.status) result = result.filter(r => r.status === filters.status)
    if (filters.cor) result = result.filter(r => r.CollectionColor?.name === filters.cor)
    setFiltered(result)
  }, [items, filters])

  const expandAll = useCallback(() => {
    const allKeys: Record<string, boolean> = {}
    const keyField = currentView === 'op' ? 'serviceId' : 'fashionOrderId'
    filtered.forEach(item => { allKeys[String((item as any)[keyField])] = true })
    setExpandedGroups(allKeys)
  }, [currentView, filtered])

  const collapseAll = useCallback(() => {
    const allKeys: Record<string, boolean> = {}
    const keyField = currentView === 'op' ? 'serviceId' : 'fashionOrderId'
    filtered.forEach(item => { allKeys[String((item as any)[keyField])] = false })
    setExpandedGroups(allKeys)
  }, [currentView, filtered])

  const exportCSV = useCallback(() => {
    const headers = ['ID', 'OP', 'Referencia produto', 'Codigo Produto', 'Nome do Produto', 'Cor', 'Tam', 'Status', 'Pedido', 'EP', 'Colecao']
    const rows = [headers.join(';')]
    filtered.forEach(r => {
      const op = `OP-${String(r.serviceId).padStart(3, '0')}`
      const ref = r.FashionProduct?.referenceProduct ?? ''
      const code = r.FashionProduct?.productCode ?? ''
      const mod = r.FashionProduct?.name ?? ''
      const cor = r.CollectionColor?.name ?? ''
      const ep = r.FashionProduct?.Episode?.name ?? ''
      const col = r.FashionProduct?.Collection?.name ?? ''
      rows.push([r.id, op, ref, code, mod, cor, r.size, STATUS_LABELS[r.status] || r.status, r.fashionOrderId, ep, col].join(';'))
    })
    const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `estoque-vendas-${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
  }, [filtered])

  const toolbarState: SalesStockState = {
    stats, currentView, setCurrentView, filters, setFilters, filterOptions,
    filteredCount: filtered.length, fetchData, expandAll, collapseAll, exportCSV,
  }

  return { items, filtered, isLoading, expandedGroups, setExpandedGroups, toolbarState }
}

export default SalesStockDashboard
